import { Button, makeStyles, Text } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { isEmpty } from 'es-toolkit/compat';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useId } from 'react';
import { useStores } from '../../../common/hooks';
import { getUserNames } from '../../../common/string/get-user-names';
import {
  formatEmailAddresses,
  parseEmailAddresses,
} from '../../../shared/functions';
import { FlexBox } from '../flex-box';
import { FormInput } from '../form/FormInput';
import { ValidationMessageList } from '../ValidationMessageList';
import {
  LeadAssignmentInputType,
  LeadAssignmentLead,
  LeadAssignmentType,
} from './lead-assignment.types';
import './LeadAssignment.scss';

interface LeadAssignmentProps {
  leads: LeadAssignmentLead[];
  title: string;
  isPrimaryLead: boolean;
  onDeleteLead(lead: LeadAssignmentLead): void;
  allowMultipleEmailAddresses: boolean;
  requiresMicrosoftEmailAddress?: boolean;
  disabled?: boolean;
  titleTooltipText?: string;
  onAddLead?: (emailAddress: string) => void;
  onAddLeads?: (emailAddresses: string[]) => void;
}

const useStyles = makeStyles({
  inputContainer: {
    padding: 0,
  }
});

const LeadAssignmentImpl: React.FC<LeadAssignmentProps> = (props) => {
  const {
    leads,
    title,
    isPrimaryLead,
    onAddLead,
    onDeleteLead,
    allowMultipleEmailAddresses,
    disabled,
    titleTooltipText,
    requiresMicrosoftEmailAddress = false,
    onAddLeads,
  } = props;

  const {
    leadAssignmentUiStore,
  } = useStores();

  const styles = useStyles();
  const storeKey = useId();
  const emailAddressInput = leadAssignmentUiStore.getEmailAddressInput(storeKey);
  const isBeingValidated = leadAssignmentUiStore.getIsBeingValidated(storeKey);
  const leadsToDisplay = isPrimaryLead
    ? leadAssignmentUiStore.getPrimaryLeadsToDisplay(leads)
    : leadAssignmentUiStore.getSecondaryLeadsToDisplay(leads);
  const emailAddressValidationMessages = leadAssignmentUiStore.getEmailAddressValidationMessages(storeKey);
  const hasExistingPrimaryLeadValidationMessages = leadAssignmentUiStore.getHasExistingPrimaryLeadValidationMessages(storeKey);

  const handleDelete = (lead: LeadAssignmentLead): void => {
    leadAssignmentUiStore.setIsBeingValidated(storeKey, false);
    onDeleteLead(lead);
  };

  const handleAddLead = (): void => {
    if (!onAddLead) return;

    const emailAddress = leadAssignmentUiStore.getEmailAddressToAdd(storeKey, leads);
    if (!emailAddress) return;

    onAddLead(emailAddress);
  };

  const handleAddLeads = (): void => {
    if (!onAddLeads) return;

    const emailAddresses = leadAssignmentUiStore.getEmailAddressesToAdd(storeKey, leads);
    if (isEmpty(emailAddresses)) return;

    onAddLeads(emailAddresses);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    if (!allowMultipleEmailAddresses) return;

    const pastedText = event.clipboardData.getData('text/plain');
    if (!pastedText) return;

    const pastedEmailAddresses = parseEmailAddresses(pastedText);
    if (isEmpty(pastedEmailAddresses)) return;

    const selectionStart = event.currentTarget.selectionStart;
    const selectionEnd = event.currentTarget.selectionEnd;
    if (selectionStart === null || selectionEnd === null) return;

    event.preventDefault();

    event.currentTarget.setRangeText(
      formatEmailAddresses(pastedEmailAddresses),
      selectionStart,
      selectionEnd,
      'end',
    );
    leadAssignmentUiStore.setEmailAddressInput(storeKey, event.currentTarget.value);
  };

  const preloadData = (): void => {
    leadAssignmentUiStore.setEmailAddressInput(storeKey, '');
    leadAssignmentUiStore.setIsBeingValidated(storeKey, false);
    leadAssignmentUiStore.setLeadAssignmentType(
      storeKey,
      isPrimaryLead ? LeadAssignmentType.Primary : LeadAssignmentType.Secondary,
    );
    leadAssignmentUiStore.setLeadAssignmentInputType(
      storeKey,
      allowMultipleEmailAddresses ? LeadAssignmentInputType.Multiple : LeadAssignmentInputType.Single,
    );
    leadAssignmentUiStore.setRequiresMicrosoftEmailAddress(
      storeKey,
      requiresMicrosoftEmailAddress,
    );
  };

  useEffect(() => {
    preloadData();
    return () => {
      leadAssignmentUiStore.resetStates(storeKey);
    };
  }, [leads]);

  const renderDisclaimer = (): React.JSX.Element => (
    <Text size={200}>Please add <Text italic size={200} className='text--blue'>alias@microsoft.com</Text>. Do not use firstname.lastname format.</Text>
  );

  return (
    <FlexBox
      isFullWidth
      direction='column'
      className='lead-assignment'
    >
      <FormInput
        descriptionContent={renderDisclaimer()}
        label={title}
        tooltipText={titleTooltipText}
        validationMessage={isBeingValidated
          && emailAddressValidationMessages.length
          ? <ValidationMessageList
              messages={emailAddressValidationMessages}
            />
          : undefined}
        onChange={(value) => leadAssignmentUiStore.setEmailAddressInput(storeKey, value)}
        inputProps={{
          value: emailAddressInput,
          disabled,
          className: styles.inputContainer,
          onPaste: handlePaste,
          contentAfter: allowMultipleEmailAddresses
            ? (
              <Button
                appearance='transparent'
                disabled={disabled}
                onClick={handleAddLeads}
              >
                Add
              </Button>
            )
            : (
              <Button
                appearance='transparent'
                disabled={disabled}
                onClick={handleAddLead}
              >
                Add
              </Button>
            ),
        }}
      />
      {
        leadsToDisplay.map((lead, index) => {
          return (
            <FlexBox
              aligns='center'
              className='lead-assignment__added-lead'
              key={index}
              content='space-between'
            >
              <Text>
                {lead.user ? getUserNames([lead.user]) : lead.emailAddress}
              </Text>
              <Button
                aria-label={`Remove ${lead.emailAddress}`}
                appearance='transparent'
                onClick={() => handleDelete(lead)}
                disabled={disabled}
                icon={<DismissRegular />}
              />
            </FlexBox>
          );
        })
      }
      {
        isBeingValidated
        && hasExistingPrimaryLeadValidationMessages.length
          ? <ValidationMessageList
              messages={hasExistingPrimaryLeadValidationMessages}
            />
          : undefined
      }
    </FlexBox>
  );
};

export const LeadAssignment = observer(LeadAssignmentImpl);
